/**
 * Main game state store using Zustand
 * Manages all game state with persistence
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  PlayerState,
  PlayerStats,
  FishingState,
  FishInstance,
  EquippedGear,
  Inventory,
  Quality,
  QuestDefinition,
  TrapState,
} from '@/types';
import { createDefaultPlayerState, createDefaultPlayerStats } from '@/data';

/**
 * UI state (not persisted)
 */
interface UIState {
  fishingState: {
    status: FishingState;
    timeRemaining: number;
    startedAt: number | null;
  };
  currentTab: 'fishing' | 'inventory' | 'outfitters' | 'markets' | 'buddies' | 'stats' | 'milestones';
  targetMarketId?: string; // Market to auto-open when navigating to markets tab
  activeQuestSell?: string; // Quest ID for active sell quest panel
  pendingQuestOffer: {
    completedQuestId: string;
    nextQuest: QuestDefinition;
  } | null;
  newlyUnlockedQuests: Array<{ questId: string; questName: string }>;
}

/**
 * Game store state interface
 */
interface GameStore extends UIState {
  // Player state (persisted)
  playerState: PlayerState;

  // Derived stats (computed)
  playerStats: PlayerStats;

  // === STATE ACTIONS ===

  // Player state setters
  setPlayerState: (state: PlayerState) => void;

  // Currency
  addCurrency: (amount: number) => void;

  // Inventory
  setInventory: (inventory: Inventory) => void;
  addFishToInventory: (fish: FishInstance) => boolean;
  removeFishFromInventory: (definitionId: string, quality: Quality, count: number) => boolean;
  toggleFishProtection: (definitionId: string, quality: Quality) => void;
  isFishProtected: (definitionId: string, quality: Quality) => boolean;
  setAutoSellThreshold: (quality: Quality | null) => void;

  // Fish Discovery
  trackFishDiscovery: (fish: FishInstance) => void;

  // Equipment
  setEquippedGear: (gear: EquippedGear) => void;
  addOwnedEquipment: (id: string, data: { ownedAt: number; quantity: number; timesUsed?: number }) => void;

  // Stats
  incrementStat: (stat: keyof PlayerState['stats'], amount?: number) => void;

  // Derived stats
  setPlayerStats: (stats: PlayerStats) => void;

  // UI state
  setFishingState: (state: Partial<UIState['fishingState']>) => void;
  setCurrentTab: (tab: UIState['currentTab']) => void;

  // Quest panel UI state
  setPendingQuestOffer: (offer: UIState['pendingQuestOffer']) => void;
  clearPendingQuestOffer: () => void;
  addNewlyUnlockedQuest: (questId: string, questName: string) => void;
  dismissNewlyUnlockedQuest: (questId: string) => void;

  // Milestones
  addEarnedMilestone: (milestoneId: string) => void;

  // Trap state
  setTrapState: (updates: Partial<TrapState>) => void;
  setTrapBait: (baitId: string | null) => void;

  // Utilities
  resetGame: () => void;
}

/**
 * Create the game store
 */
export const useGameStore = create<GameStore>()(
  immer((set, get) => ({
    // === INITIAL STATE ===
    playerState: createDefaultPlayerState(),
    playerStats: createDefaultPlayerStats(),

    fishingState: {
      status: FishingState.IDLE,
      timeRemaining: 0,
      startedAt: null,
    },

    currentTab: 'fishing',
    pendingQuestOffer: null,
    newlyUnlockedQuests: [],

    // === ACTIONS ===

    setPlayerState: (newState) =>
      set((draft) => {
        draft.playerState = newState;
      }),

    addCurrency: (amount) =>
      set((draft) => {
        draft.playerState.currency += amount;
        if (amount > 0) {
          draft.playerState.stats.totalCurrencyEarned += amount;
        }
      }),

    setInventory: (inventory) =>
      set((draft) => {
        draft.playerState.inventory = inventory;
      }),

    addFishToInventory: (fish) => {
      let added = false;

      set((draft) => {
        const inv = draft.playerState.inventory;

        // Check capacity
        if (inv.currentCount >= inv.capacity) {
          return;
        }

        // Find existing stack INSIDE the draft
        const existingStackIndex = inv.stacks.findIndex(
          (stack) =>
            stack.definitionId === fish.definitionId &&
            stack.quality === fish.quality
        );

        if (existingStackIndex >= 0) {
          inv.stacks[existingStackIndex].count += 1;
        } else {
          inv.stacks.push({
            definitionId: fish.definitionId,
            quality: fish.quality,
            count: 1,
          });
        }

        inv.currentCount += 1;
        added = true;
      });

      return added;
    },

    removeFishFromInventory: (definitionId, quality, count) => {
      let removed = false;

      set((draft) => {
        const inv = draft.playerState.inventory;
        const stackIndex = inv.stacks.findIndex(
          (stack) =>
            stack.definitionId === definitionId && stack.quality === quality
        );

        if (stackIndex === -1) return;

        const targetStack = inv.stacks[stackIndex];
        if (targetStack.count < count) return;

        targetStack.count -= count;
        inv.currentCount -= count;

        if (targetStack.count === 0) {
          inv.stacks.splice(stackIndex, 1);
        }

        removed = true;
      });

      return removed;
    },

    toggleFishProtection: (definitionId, quality) =>
      set((draft) => {
        const protectedFish = draft.playerState.autoSellSettings.protectedFish;

        if (!protectedFish[definitionId]) {
          protectedFish[definitionId] = {};
        }

        const isCurrentlyProtected = protectedFish[definitionId][quality];
        if (isCurrentlyProtected) {
          delete protectedFish[definitionId][quality];
          // Clean up empty fish entry
          if (Object.keys(protectedFish[definitionId]).length === 0) {
            delete protectedFish[definitionId];
          }
        } else {
          protectedFish[definitionId][quality] = true;
        }
      }),

    isFishProtected: (definitionId, quality) => {
      const state = get();
      return state.playerState.autoSellSettings.protectedFish[definitionId]?.[quality] === true;
    },

    setAutoSellThreshold: (quality) =>
      set((draft) => {
        draft.playerState.autoSellSettings.qualityThreshold = quality ?? undefined;
      }),

    trackFishDiscovery: (fish) =>
      set((draft) => {
        const discoveries = draft.playerState.discoveredFish;

        // Check if species already discovered
        let discovery = discoveries.find(d => d.definitionId === fish.definitionId);

        const qualityOrder = [Quality.COMMON, Quality.UNCOMMON, Quality.RARE, Quality.EPIC, Quality.LEGENDARY, Quality.UNIQUE, Quality.QUEST];
        const qualityValue = (q: Quality) => qualityOrder.indexOf(q);

        if (!discovery) {
          // First time seeing this species - initialize records
          discovery = {
            definitionId: fish.definitionId,
            discoveredAt: Date.now(),
            qualitiesSeen: [fish.quality],
            records: {
              maxLength: fish.length,
              maxWeight: fish.weight,
              bestQuality: fish.quality,
            },
          };
          discoveries.push(discovery);
          console.log(`🎉 NEW SPECIES DISCOVERED: ${fish.definitionId}!`);
        } else {
          // Update quality tracking
          if (!discovery.qualitiesSeen.includes(fish.quality)) {
            discovery.qualitiesSeen.push(fish.quality);
            console.log(`✨ New quality discovered for ${fish.definitionId}: ${fish.quality}`);
          }

          // Update records
          let recordBroken = false;

          if (fish.length > discovery.records.maxLength) {
            discovery.records.maxLength = fish.length;
            console.log(`📏 NEW LENGTH RECORD for ${fish.definitionId}: ${fish.length}"`);
            recordBroken = true;
          }

          if (fish.weight > discovery.records.maxWeight) {
            discovery.records.maxWeight = fish.weight;
            console.log(`⚖️ NEW WEIGHT RECORD for ${fish.definitionId}: ${fish.weight} oz`);
            recordBroken = true;
          }

          if (qualityValue(fish.quality) > qualityValue(discovery.records.bestQuality)) {
            discovery.records.bestQuality = fish.quality;
            console.log(`⭐ NEW QUALITY RECORD for ${fish.definitionId}: ${fish.quality}`);
            recordBroken = true;
          }

          if (recordBroken) {
            console.log(`🏆 Record broken for ${fish.definitionId}!`);
          }
        }
      }),

    setEquippedGear: (gear) =>
      set((draft) => {
        draft.playerState.equippedGear = gear;
      }),

    addOwnedEquipment: (id, data) =>
      set((draft) => {
        draft.playerState.ownedEquipment[id] = data;
      }),

    incrementStat: (stat, amount = 1) =>
      set((draft) => {
        if (typeof draft.playerState.stats[stat] === 'number') {
          (draft.playerState.stats[stat] as number) += amount;
        }
      }),

    setPlayerStats: (stats) => set({ playerStats: stats }),

    setFishingState: (state) =>
      set((draft) => {
        draft.fishingState = { ...draft.fishingState, ...state };
      }),

    setCurrentTab: (tab) => set({ currentTab: tab }),

    setPendingQuestOffer: (offer) => set({ pendingQuestOffer: offer }),

    clearPendingQuestOffer: () => set({ pendingQuestOffer: null }),

    addNewlyUnlockedQuest: (questId, questName) =>
      set((draft) => {
        // Avoid duplicates
        if (!draft.newlyUnlockedQuests.some((q) => q.questId === questId)) {
          draft.newlyUnlockedQuests.push({ questId, questName });
        }
      }),

    dismissNewlyUnlockedQuest: (questId) =>
      set((draft) => {
        draft.newlyUnlockedQuests = draft.newlyUnlockedQuests.filter(
          (q) => q.questId !== questId
        );
      }),

    addEarnedMilestone: (milestoneId) =>
      set((draft) => {
        if (!draft.playerState.earnedMilestones) {
          draft.playerState.earnedMilestones = [];
        }
        if (!draft.playerState.earnedMilestones.includes(milestoneId)) {
          draft.playerState.earnedMilestones.push(milestoneId);
        }
      }),

    setTrapState: (updates) =>
      set((draft) => {
        Object.assign(draft.playerState.trapState, updates);
      }),

    setTrapBait: (baitId) =>
      set((draft) => {
        draft.playerState.trapState.selectedTrapBait = baitId;
      }),

    resetGame: () =>
      set({
        playerState: createDefaultPlayerState(),
        playerStats: createDefaultPlayerStats(),
        fishingState: {
          status: FishingState.IDLE,
          timeRemaining: 0,
          startedAt: null,
        },
        currentTab: 'fishing',
        pendingQuestOffer: null,
        newlyUnlockedQuests: [],
      }),
  }))
);
